<script setup lang="ts">
// swiper
import { Swiper, SwiperSlide } from 'swiper/vue';
import 'swiper/css';

// components
import Forms from '~/components/redesign/Forms';
import Cards from '~/components/redesign/Cards';
import Buttons from '~/components/redesign/Buttons';
import AppTag from '~/components/redesign/AppTag.vue';
import AppAvatar from '~/components/redesign/AppAvatar.vue';
import TableOfContent from '~/components/redesign/TableOfContent.vue';
import AppBreadcrumbs from '~/components/redesign/AppBreadcrumbs.vue';
import CopyToClipboard from '~/components/redesign/CopyToClipboard.vue';
import ArticleMarkup from '~/components/redesign/ArticleMarkup.vue';

// composables
import useLocalePathWithSlash from '~/composables/useLocalePathWithSlash';
import { useDayjs } from '#dayjs';

// utils
import get from 'lodash.get';
import { isString, getInitials, convertArticleToCardsBlogProps } from '~/utils';

// constants
import { PATHS, STATUS_CODES } from '~/constants';

// types
import type { AppTagProps } from '~/components/redesign/AppTag.vue';
import { PostStatus } from '~/types';

const TAG_COLORS: Record<string, AppTagProps['variant']> = {
  technical: 'secondary',
  business: 'primary',
} as const;

const { href } = useRequestURL();
const {
  params: { slug },
  query: { preview },
} = useRoute();

const dayjs = useDayjs();
const localePathWithSlash = useLocalePathWithSlash();

const { data: blogArticleData } = await useAsyncGql({
  operation: 'BlogArticle',
  variables: {
    slug: slug as string,
    status: preview ? undefined : PostStatus.PUBLISHED,
  },
  options: {
    transform: response => get(response, 'blog_article'),
  },
});

if (!blogArticleData.value) {
  throw createError({
    status: STATUS_CODES.NOT_FOUND,
    message: 'Article not found',
  });
}

const article = blogArticleData.value;
const articleId = useState('article-id', () => get(article, 'id'));
const title = get(article, 'data.title');
const subtitle = get(article, 'data.data.post_subtitle');
const tag = computed(() => {
  const _tag = get(article, 'categories[0]');
  const slug = get(_tag, 'slug');

  return {
    slug,
    title: get(_tag, 'data.title'),
    color: TAG_COLORS[slug],
    href: localePathWithSlash(slug),
  };
});
const author = get(article, 'authors[0]');
const authorAvatar = useState('author-avatar', () => {
  return get(author, 'data.data.avatar');
});
const authorName = useState('author-name', () => get(author, 'data.name'));
const authorLink = useState('author-slug', () =>
  localePathWithSlash(`${PATHS.AUTHORS.path}/${get(author, 'slug', '')}`),
);
const publishedAt = useState('publish-at', () => get(article, 'publish_at'));
const formattedPublishedAt = dayjs(publishedAt.value).format('MMM Do YYYY');
const timeToRead = useState('time-to-read', () => {
  return get(article, 'data.data.preview_time_to_read');
});
const articleMarkup = get(article, 'data.content');
const sourceLinks = get(article, 'data.data.source_links');
const inThisSeries = get(article, 'relatedArticles', []);
const isTableOfContentVisible = get(article, 'data.data.show_sidebar_nav');
const tableOfContent = get(article, 'data.content_table');

const { data: blogArticlePreviewsFilteredData } = await useAsyncGql({
  operation: 'BlogArticlesPreviewsFiltered',
  variables: {
    first: 3,
    category_slugs: [tag.value.slug],
    exclude: [articleId.value],
    statuses: preview
      ? [PostStatus.DRAFT, PostStatus.SCHEDULED]
      : [PostStatus.PUBLISHED],
  },
  options: {
    transform: response => get(response, 'blog_articles.data', []),
  },
});

const relatedBlogs = blogArticlePreviewsFilteredData.value || [];

const subscribeMessage = ref<string>();
const setSubscribeMessage = (message: string) => {
  subscribeMessage.value = message;
};

const CATEGORY_SLUG_TO_PATH = {
  technical: PATHS.TECHNICAL,
  business: PATHS.BUSINESS,
} as const;

useSeoMeta({
  title: get(article, 'data.seo.title'),
  ogTitle: get(article, 'data.seo.title'),
  description: get(article, 'data.seo.description'),
  ogDescription: get(article, 'data.seo.description'),
  ogImage: get(article, 'data.seo.image'),
  ogType: get(article, 'data.seo.og_type'),
  author: get(author, 'data.name'),
  articlePublishedTime: publishedAt,
});
</script>

<template>
  <article data-page-name="blog-article">
    <div class="external-wrapper post">
      <AppBreadcrumbs
        v-if="isString(title)"
        :items="[CATEGORY_SLUG_TO_PATH[tag.slug], title]"
        append
        accent="light"
      />

      <div class="middle-wrapper">
        <AppTag :href="tag.href" :variant="tag.color">
          {{ tag.title }}
        </AppTag>

        <h1 class="title">{{ title }}</h1>
        <strong class="subtitle">{{ subtitle }}</strong>

        <div class="author">
          <div class="left">
            <AppAvatar
              v-if="isString(authorName)"
              :src="authorAvatar"
              :alt="`${authorName} avatar`"
              :initials="getInitials(authorName)"
              size="3rem"
            />

            <div class="about">
              <NuxtLink :to="localePathWithSlash(authorLink)" target="_blank">
                <strong>{{ authorName }}</strong>
              </NuxtLink>

              <time
                :datetime="publishedAt"
                :title="formattedPublishedAt"
                pubdate
              >
                {{ formattedPublishedAt }}
              </time>
              <span v-if="isString(timeToRead)">
                &middot; {{ timeToRead }} {{ $t('min_to_read') }}
              </span>
            </div>
          </div>

          <div class="right">
            <strong>{{ $t('Share') }}:</strong>
            <CopyToClipboard :source="href" />
          </div>
        </div>
      </div>

      <div class="content">
        <transition appear name="fade">
          <TableOfContent
            v-if="isTableOfContentVisible && Array.isArray(tableOfContent)"
            :items="tableOfContent"
          >
            <template #title>
              <strong class="title">
                {{ $t('Table_of_Contents') }}
              </strong>
            </template>

            <template #item="{ item, active }">
              <Buttons.Link
                :to="item.href"
                :class="{ active }"
                size="small"
                accent="black"
                fill="none"
              >
                <span v-html="item.title" />
              </Buttons.Link>
            </template>
          </TableOfContent>
        </transition>

        <ArticleMarkup v-if="isString(articleMarkup)" :markup="articleMarkup" />
      </div>
    </div>

    <section v-if="sourceLinks" id="source-links">
      <div class="inner-wrapper">
        <h2>{{ $t('Source_Links') }}</h2>

        <ArticleMarkup v-if="isString(sourceLinks)" :markup="sourceLinks" />
      </div>
    </section>

    <section v-if="inThisSeries.length" class="in-this-series">
      <div class="inner-wrapper">
        <h3>{{ $t('In_this_series') }}</h3>

        <ul>
          <li v-for="_article of inThisSeries" :key="_article.slug">
            <Cards.Blog
              v-bind="convertArticleToCardsBlogProps(_article)"
              orientation="horizontal"
            />
          </li>
        </ul>
      </div>
    </section>

    <section class="product-news">
      <div class="inner-wrapper">
        <h4>{{ $t('Product_news') }}</h4>
        <Forms.Subscribe @change="setSubscribeMessage" />

        <transition name="fade">
          <div v-if="isString(subscribeMessage)" class="subscribe-message">
            <span v-html="subscribeMessage" />
          </div>
        </transition>
      </div>
    </section>

    <section v-if="relatedBlogs.length" class="related-blogs">
      <div class="external-wrapper">
        <h3>{{ $t('Related_Blogs') }}</h3>

        <ul class="cards">
          <li v-for="_article of relatedBlogs" :key="_article.id" class="card">
            <Cards.Blog
              v-bind="convertArticleToCardsBlogProps(_article)"
              orientation="vertical"
            />
          </li>
        </ul>

        <Swiper
          :breakpoints="{
            767: {
              spaceBetween: 24,
              slidesOffsetBefore: 32,
              slidesOffsetAfter: 32,
            },
            0: {
              spaceBetween: 8,
              slidesOffsetBefore: 16,
              slidesOffsetAfter: 16,
            },
          }"
          slides-per-view="auto"
        >
          <SwiperSlide v-for="_article of relatedBlogs" :key="_article.id">
            <Cards.Blog
              v-bind="convertArticleToCardsBlogProps(_article)"
              orientation="vertical"
            />
          </SwiperSlide>
        </Swiper>
      </div>
    </section>
  </article>
</template>

<style scoped lang="scss">
@use '_/mixins/size';
@use '_/mixins/flex';
@use '_/mixins/typo';
@use '_/mixins/media';
@use '_/mixins/position';
@use '_/mixins/component';
@use '_/fn';

.external-wrapper {
  padding: 0 2rem;
  @include size.wrapper(80rem);

  @include media.mobile {
    padding: 0 1rem;
  }
}

.inner-wrapper {
  @include size.wrapper(37rem);

  @include media.tablet {
    padding: 0 2rem;
    @include size.wrapper(100%);
  }

  @include media.mobile {
    padding: 0 1rem;
  }
}

[data-page-name='blog-article'] {
  padding-top: 2rem;

  @include media.tablet {
    padding-top: 1.5rem;
  }

  .post {
    [data-component-name='AppBreadcrumbs'] {
      align-self: flex-start;
      margin-bottom: 3rem;

      @include media.tablet {
        margin-bottom: 2rem;
      }

      @include media.mobile {
        margin-bottom: 1.5rem;
      }
    }

    .middle-wrapper {
      @include size.wrapper(50rem);

      @include flex.center-start;
      flex-direction: column;
      margin-bottom: 2rem;

      @include media.tablet {
        @include size.wrapper(100%);
        margin-bottom: 1.5rem;
      }

      @include media.mobile {
        margin-bottom: 0.5rem;
      }

      [data-component-name='AppTag'] {
        margin-bottom: 0.5rem;

        @include media.tablet {
          margin-bottom: 0.6rem;
        }
      }

      h1.title {
        @include typo.h3;
        text-align: center;
        margin-bottom: 0.5rem;
      }

      strong.subtitle {
        @include typo.subtitle-1;
        margin-bottom: 1rem;

        @include media.mobile {
          margin-bottom: 0.5rem;
        }
      }

      .author {
        align-self: stretch;
        @include flex.center-sb;

        padding: 1rem 0;
        border-bottom: 1px solid fn.token('outline-secondary');

        @include media.mobile {
          gap: 0.5rem 0;
          padding: 0.5rem 0;
          flex-direction: column;
        }

        .left {
          @include flex.start-start;
          gap: 0 0.5rem;

          .about {
            a {
              display: block;
              @include typo.subtitle-2;

              &:hover {
                text-decoration: underline;
              }
            }

            time,
            span {
              @include typo.body-4;
              color: fn.token('text-secondary');
            }
          }
        }

        .right {
          @include flex.center-start;
          gap: 0 0.5rem;

          @include media.tablet {
            margin-right: 1rem;
          }

          strong {
            @include typo.subtitle-2;
            color: fn.token('text-secondary');
          }
        }
      }
    }

    .content {
      $toc: '[data-component-name=' TableOfContent ']';

      &:not(:has(#{$toc})) {
        @include flex.start-center;
      }

      &:has(#{$toc}) {
        @include flex.start-start;
        gap: 0 2rem;
      }

      #{$toc} {
        @include size.fluid-max-width(17.5rem);

        @include media.tablet {
          display: none;
        }

        strong {
          display: block;
          padding: 0.5rem;
          @include typo.subtitle-3;
        }

        [data-component-name='Buttons.Link'] {
          display: block;
          border-left: 3px solid transparent;
          transition: border-color 0.15s ease;

          span {
            margin-right: auto;
            @include typo.body-3;
            color: fn.token('text-secondary');
            transition: color 0.15s ease;
          }

          &.active {
            border-color: fn.token('outline-action');

            span {
              color: fn.token('text-primary');
            }
          }
        }
      }

      [data-component-name='ArticleMarkup'] {
        @include size.fluid-max-width(37rem);

        @include media.tablet {
          @include size.fluid-max-width(100%);
        }
      }
    }

    &:has(+ section#source-links) {
      margin-bottom: 2rem;

      @include media.tablet {
        margin-bottom: 1.5rem;
      }
    }

    &:not(:has(+ section#source-links)) {
      margin-bottom: 8rem;

      @include media.tablet {
        margin-bottom: 5rem;
      }
    }
  }

  section#source-links {
    padding-top: 5rem;
    margin: -5rem 0 8rem 0;

    @include media.tablet {
      margin-bottom: 5rem;
    }

    h2 {
      @include typo.subtitle-1;
      margin-bottom: 1rem;
    }
  }

  section.in-this-series {
    margin-bottom: 8rem;

    @include media.tablet {
      margin-bottom: 5rem;
    }

    h3 {
      @include typo.h4;
      text-align: center;
      margin-bottom: 2rem;
    }

    ul {
      li {
        border-bottom: 1px solid fn.token('outline-secondary');
      }
    }
  }

  section.product-news {
    position: relative;

    padding: 5rem 0;
    margin-bottom: 8rem;
    background-color: fn.token('surf-cont-secondary');

    @include media.tablet {
      padding: 4rem 0;
      margin-bottom: 5rem;
    }

    @include media.mobile {
      padding: 1.5rem 0;
    }

    h4 {
      @include typo.subtitle-1;
      margin-bottom: 1rem;
    }

    .subscribe-message {
      @include position.absolute-full;
      @include flex.center-center;
      background-color: fn.token('surf-cont-secondary');

      z-index: 2;

      span {
        @include typo.subtitle-1;
        text-align: center;
      }
    }
  }

  section.related-blogs {
    margin-bottom: 10.25rem;

    @include media.tablet {
      margin-bottom: 8rem;
    }

    @include media.mobile {
      margin-bottom: 5rem;
    }

    .external-wrapper {
      @include media.tablet {
        padding: 0;
      }

      h3 {
        @include typo.h4;
        text-align: center;
        margin-bottom: 2rem;
      }

      ul.cards {
        @include flex.stretch-sb;
        gap: 0 2rem;

        @include media.tablet {
          display: none;
        }

        li.card {
          width: 100%;
        }
      }

      :deep(.swiper) {
        display: none;

        @include media.tablet {
          display: block;
        }

        .swiper-wrapper {
          .swiper-slide {
            @include size.fluid-max-width(21.25rem);

            @include media.mobile {
              @include size.fluid-max-width(20.5rem);
            }

            [data-component-name='Cards.Blog'] {
              min-height: 28.125rem;
            }
          }
        }
      }
    }
  }
}
</style>
